import Cookies from 'js-cookie';
import moment from 'moment';
import axios from 'axios';

class UserManager {
  constructor(config) {
    this.authUrl = config.authUrl;
    this.responseType = config.responseType;
    this.clientId = config.clientId;
    this.redirectUri = config.redirectUri;
    this.scope = config.scope;
    this.secret = config.secret;
    this.roles = config.roles || null;
    this.website = config.website || 'Default';
    this.postLogoutRedirectUri = config.postLogoutRedirectUri;
    this.logoutUri = config.logoutUri;
    this.refresh_token = null;
    this.api_access_token = null;
    this.buffer = [];
    this.requestingNewAccessToken = false;
    this.sessionCheckInterval = config.sessionCheckInterval || 2000;

    this.interceptAddAccessTokenToHeader();
    this.interceptRetryOnUnAuthorized();

    window.addEventListener('beforeunload', () => {
      this.clearLocalStorage();
      return;
    });
  }

  interceptAddAccessTokenToHeader = () => {
    return axios.interceptors.request.use(
      config => {
        const isAuthServer = config.url.includes(process.env.REACT_APP_STS_URL);
        if (!isAuthServer) {
          const accessToken = this.getAccessToken();
          const token = accessToken !== null ? accessToken.access_token : null;
          if (token !== null) {
            // We have a user logged in, set the Bearer token header.
            config.headers['Authorization'] = `Bearer ${token}`;
          }
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );
  };

  startSessionIframe = () => {
    if (!this._started_iframe) {
      this._started_iframe = true;
      this.getWellKnown().then(wk => {
        const iframeUrl = wk.check_session_iframe;
        if (!this._session_state) {
          const idToken = this.getIdToken();
          this._session_state = idToken.session_state;
        }

        const idx = iframeUrl.indexOf('/', iframeUrl.indexOf('//') + 2);
        this._frame_origin = iframeUrl.substr(0, idx);
        this._frame = document.createElement('iframe');
        this._frame.style.display = 'none';

        this._frame.src = iframeUrl;
        document.body.appendChild(this._frame);
        window.addEventListener('message', this._message, false);
        this._startFrameTimer();
      });
    }
  };

  _startFrameTimer = () => {
    if (this._timer !== null) {
      this._stopFrameTimer();
    }

    this._timer = setInterval(() => {
      this._frame.contentWindow.postMessage(
        `${this.clientId} ${this._session_state}`,
        this._frame_origin
      );
    }, this.sessionCheckInterval);
  };

  _stopFrameTimer = () => {
    if (this._timer) {
      window.clearInterval(this._timer);
      this._timer = null;
    }
  };

  _message = e => {
    if (e.origin === this._frame_origin && e.source === this._frame.contentWindow) {
      if (e.data === 'changed') {
        this._stopFrameTimer();
        this.clearLocalStorage();
        this.signin();
      }
    }
  };

  interceptRetryOnUnAuthorized = () => {
    return axios.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        if (error.response && error.response.status === 401) {
          if (!error.response.config._retry) {
            error.response.config._retry = true;
            return new Promise(resolve => {
              const bufferLength = this.buffer.push({
                resolve,
                config: error.response.config
              });
              if (bufferLength === 1) {
                this.retryBuffer();
              }
            });
          } else {
            this.logOut();
          }
        }
        return Promise.reject(error);
      }
    );
  };

  signin = stateToSend => {
    this.getWellKnown().then(wk => {
      const authEndpoint = wk.authorization_endpoint;
      const rt = `response_type=${this.responseType}`;
      const cid = `client_id=${this.clientId}`;
      const redir = `redirect_uri=${this.redirectUri}`;
      const scope = `scope=${this.scope}`;
      const state = JSON.stringify(stateToSend);
      const stateStr = `state=${state}`;
      const website = `website=${this.website}`;
      const nonce = `N${Math.random()}${Date.now()}`;
      const nonceStr = `nonce=${nonce}`;
      const query = [rt, cid, redir, scope, stateStr, website, nonceStr].join('&');
      const url = encodeURI(`${authEndpoint}?${query}`);

      const inFiveMinutes = new Date(new Date().getTime() + 120 * 60 * 1000);
      const expire = { expires: inFiveMinutes };
      Cookies.set('um_state', state, expire);
      Cookies.set('um_nonce', nonce, expire);
      window.location = url;
    });
  };

  clearLocalStorage = () => {
    localStorage.removeItem('um_idToken');
    localStorage.removeItem('um_accessToken');
    localStorage.removeItem('um_wellKnown');
    localStorage.removeItem('um_idTokenPayload');
  };

  getIdToken = () => {
    const token = localStorage.getItem('um_idToken');
    try {
      return JSON.parse(token);
    } catch (ex) {
      return null;
    }
  };

  getAccessToken = () => {
    const token = localStorage.getItem('um_accessToken');
    try {
      return JSON.parse(token);
    } catch (ex) {
      return null;
    }
  };

  getIdTokenPayload = () => {
    const token = localStorage.getItem('um_idTokenPayload');
    try {
      return JSON.parse(token);
    } catch (ex) {
      return null;
    }
  };

  hasProductPermission = () => {
    // Verify roles.
    // STS returns 'role' only if the user has any roles.
    // If user has one role, the value is a string of the number; role: '101'
    // If user has multiple roles, the value is a list of strings of numbers; role: ['101', '102']
    if (this.roles !== null) {
      const idTokenPayload = this.getIdTokenPayload();
      if (idTokenPayload.role) {
        if (typeof idTokenPayload.role === 'string') {
          if (this.roles.length === 1 && this.roles[0] !== idTokenPayload.role) {
            return false;
          } else if (this.roles.length > 1) {
            return false;
          }
        } else {
          for (let i = 0; i < this.roles.length; ++i) {
            if (!idTokenPayload.role.includes(this.roles[i])) {
              return false;
            }
          }
        }
      } else if (this.roles.length > 0) {
        return false;
      }
    }
    return true;
  };

  retryBuffer = async () => {
    const success = await this.requestNewAccessToken();
    if (success) {
      for (let i = 0; i < this.buffer.length; i++) {
        const item = this.buffer[i];
        axios(item.config).then(response => item.resolve(response));
      }
      this.buffer = [];
    } else {
      this.logOut();
    }
  };

  requestNewAccessToken = () => {
    return new Promise(resolve => {
      let token = null;
      if (this.refresh_token !== null) {
        token = this.refresh_token;
      } else {
        token = this.getAccessToken();
        if (token !== null) {
          token = token.refresh_token;
          this.refresh_token = token;
        }
      }

      if (token !== null) {
        return this.getAccessTokenByRefreshToken(token).then(data => {
          if (!data.error) {
            this.refresh_token = data.refresh_token;
            this.api_access_token = data.access_token;
            localStorage.setItem('um_accessToken', JSON.stringify(data));
            resolve(true);
          } else {
            resolve(false);
            this.logOut();
          }
        });
      }
      resolve(false);
    });
  };

  getWellKnown = () => {
    return new Promise(resolve => {
      const wk = localStorage.getItem('um_wellKnown');
      if (wk === null) {
        axios(`${this.authUrl}/.well-known/openid-configuration`).then(response => {
          const data = response.data || {};
          localStorage.setItem('um_wellKnown', JSON.stringify(data));
          resolve(data);
        });
      } else {
        resolve(JSON.parse(wk));
      }
    });
  };

  getAccessTokenByRefreshToken = refreshToken => {
    return new Promise((resolve, reject) => {
      this.getWellKnown()
        .then(wk => {
          const auth = btoa(`${this.clientId}:${this.secret}`);
          const headers = {
            Authorization: `Basic ${auth}`,
            'Content-Type': 'application/x-www-form-urlencoded'
          };
          const gt = 'grant_type=refresh_token';
          const refreshTokenStr = `refresh_token=${refreshToken}`;
          const scope = `scope=${encodeURI(this.scope)}`;
          const client = `client_id=${this.clientId}`;
          const query = [gt, refreshTokenStr, scope, client].join('&');

          const options = {
            headers: headers,
            method: 'post',
            data: query
          };

          return axios(wk.token_endpoint, options);
        })
        .then(response => resolve(response.data))
        .catch(ex => reject(ex));
    });
  };

  getAccessTokenByCode = code => {
    return new Promise(resolve => {
      this.getWellKnown()
        .then(wk => {
          const auth = btoa(`${this.clientId}:${this.secret}`);
          const headers = {
            Authorization: `Basic ${auth}`,
            'Content-Type': 'application/x-www-form-urlencoded'
          };
          const gt = 'grant_type=authorization_code';
          const codeStr = `code=${code}`;
          const clientId = `client_id=${encodeURI(this.clientId)}`;
          const rediruri = `redirect_uri=${encodeURI(this.redirectUri)}`;
          const query = [gt, codeStr, clientId, rediruri].join('&');

          const options = {
            headers: headers,
            method: 'post',
            data: query
          };

          return axios(wk.token_endpoint, options);
        })
        .then(response => resolve(response.data));
    });
  };

  _decodeClientIDToken = token => {
    let result = {};
    const tokenParts = token.split('&');
    for (let i = 0; i < tokenParts.length; ++i) {
      const parts = tokenParts[i].split('=');
      if (parts[0] === 'scope') {
        result[parts[0]] = decodeURI(parts[1]);
      } else if (parts[0] === 'state') {
        result[parts[0]] = decodeURIComponent(parts[1]);
      } else {
        result[parts[0]] = parts[1];
      }
    }
    return result;
  };

  _urlBase64Decode = str => {
    let output;
    try {
      output = str.replace('-', '+').replace('_', '/');
    } catch (ex) {
      return '';
    }

    switch (output.length % 4) {
      case 0:
        break;
      case 2:
        output += '==';
        break;
      case 3:
        output += '=';
        break;
      default:
        throw new Error('Illegal base64url string!');
    }
    return window.atob(output);
  };

  _utf8_decode = utftext => {
    let string = '';
    let i = 0;
    let c = 0;
    let c2 = 0;
    let c3;

    while (i < utftext.length) {
      c = utftext.charCodeAt(i);

      if (c < 128) {
        string += String.fromCharCode(c);
        i += 1;
      } else if (c > 191 && c < 224) {
        c2 = utftext.charCodeAt(i + 1);
        string += String.fromCharCode(((c & 31) << 6) | (c2 & 63));
        i += 2;
      } else {
        c2 = utftext.charCodeAt(i + 1);
        c3 = utftext.charCodeAt(i + 2);
        string += String.fromCharCode(((c & 15) << 12) | ((c2 & 63) << 6) | (c3 & 63));
        i += 3;
      }
    }
    return string;
  };

  _getDataFromToken = token => {
    if (!token) return '';
    let data = {};
    const encoded = token.split('.')[1];
    let decoded = this._urlBase64Decode(encoded);
    decoded = this._utf8_decode(decoded);

    if (decoded !== '') {
      data = JSON.parse(decoded);
    }
    return data;
  };

  loginCallback = hash => {
    return new Promise((resolve, reject) => {
      const cbStr = hash.substr(1);
      const storedState = Cookies.get('um_state');
      const storedNonce = Cookies.get('um_nonce');
      const tokenData = this._decodeClientIDToken(cbStr);
      const resultState = decodeURIComponent(tokenData.state);
      const idTokenPayload = this._getDataFromToken(tokenData.id_token);

      if (!tokenData.error) {
        if (resultState !== storedState) {
          reject({ error: 'state mismatch when parsing token' });
        } else if (storedNonce !== idTokenPayload.nonce) {
          reject({ error: 'nonce mismatch when parsing token' });
        } else {
          const currentDate = new Date();
          const expires = new Date(idTokenPayload.exp * 1000);
          if (currentDate > expires) {
            reject({ error: 'token is expired' });
          } else {
            this.getAccessTokenByCode(tokenData.code)
              .then(data => {
                localStorage.setItem('um_idToken', JSON.stringify(tokenData));
                localStorage.setItem('um_accessToken', JSON.stringify(data));
                localStorage.setItem('um_idTokenPayload', JSON.stringify(idTokenPayload));
                this.refresh_token = data.refresh_token;
                this.api_access_token = data.access_token;
                this.startSessionIframe();
                try {
                  resolve(JSON.parse(tokenData.state).path);
                } catch (ex) {
                  resolve('/');
                }
              })
              .catch(() => reject({ error: 'Got no access token' }));
          }
        }
      } else {
        this.clearLocalStorage();
        reject({ error: 'User has no access to this site' });
      }
    });
  };

  isAuthenticated = () => {
    const currentDate = moment();
    const idToken = this.getIdTokenPayload();
    if (idToken === null) {
      return false;
    }
    const idTokenExp = moment(idToken.exp * 1000);
    if (currentDate > idTokenExp) {
      return false;
    }
    return true;
  };

  logOut = () => {
    this.getWellKnown().then(wk => {
      let idToken = this.getIdToken();
      const endPoint = wk.end_session_endpoint;
      const idTokenStr = `id_token_hint=${idToken ? idToken.id_token : ''}`;
      const postLogoutRedirect = `post_logout_redirect_uri=${encodeURI(
        this.postLogoutRedirectUri
      )}`;
      const urlStr = `${endPoint}?${idTokenStr}&${postLogoutRedirect}`;
      this.clearLocalStorage();
      window.location = urlStr;
    });
  };
}

export default UserManager;



// WEBPACK FOOTER //
// ./src/common/jsx/auth/UserManager.js